import { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxSeparator,
} from "@/components/ui/combobox";
import { FieldErrorText, Label } from "@/components/ui/field";
import { cn } from "@/lib/utils";
import {
  groupScheduleTimeOptions,
  type ScheduleTimeComboboxGroup,
  type ScheduleTimeComboboxOption,
} from "./scheduleTimeComboboxHelpers";

export type { ScheduleTimeComboboxOption } from "./scheduleTimeComboboxHelpers";

const filterOptionsByQuery = (
  options: ScheduleTimeComboboxOption[],
  query: string,
): ScheduleTimeComboboxOption[] => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return options;

  return options.filter((option) =>
    option.label.toLowerCase().includes(normalizedQuery),
  );
};

const labelForScheduleTimeValue = (
  options: ScheduleTimeComboboxOption[],
  scheduleTimeValue: string | null,
): string => {
  if (!scheduleTimeValue) return "";
  return (
    options.find((option) => option.value === scheduleTimeValue)?.label ?? ""
  );
};

const groupsWithItems = (
  groups: ScheduleTimeComboboxGroup[],
): ScheduleTimeComboboxGroup[] =>
  groups.filter((group) => group.items.length > 0);

export interface ScheduleTimeComboboxProps {
  options: ScheduleTimeComboboxOption[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  label?: string;
  labelVariant?: "default" | "secondary";
  labelSize?: "sm" | "md" | "lg";
  placeholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  /** Shows the clear control when a time is selected (replaces the chevron). */
  clearable?: boolean;
  errorMessage?: string;
  className?: string;
}

const ScheduleTimeCombobox = ({
  options,
  value,
  defaultValue,
  onValueChange,
  label,
  labelVariant = "secondary",
  labelSize = "sm",
  placeholder = "Search or select time",
  emptyMessage = "No time found.",
  disabled = false,
  clearable = true,
  errorMessage,
  className,
}: ScheduleTimeComboboxProps) => {
  const isControlled = value !== undefined;
  const comboboxInputId = useId();

  const [uncontrolledValue, setUncontrolledValue] = useState<string | null>(
    () => defaultValue ?? null,
  );

  const selectedValue = isControlled ? value || null : uncontrolledValue;
  const selectedValueRef = useRef(selectedValue);

  useEffect(() => {
    selectedValueRef.current = selectedValue;
  }, [selectedValue]);

  const selectedOption = useMemo(
    () =>
      selectedValue
        ? (options.find((option) => option.value === selectedValue) ?? null)
        : null,
    [options, selectedValue],
  );

  const [inputValue, setInputValue] = useState(() =>
    labelForScheduleTimeValue(options, selectedValue),
  );
  const [showAllOptions, setShowAllOptions] = useState(false);

  useEffect(() => {
    if (!isControlled) return;
    setInputValue(labelForScheduleTimeValue(options, value || null));
  }, [isControlled, options, value]);

  useEffect(() => {
    if (isControlled || !selectedValue || inputValue) return;
    const nextLabel = labelForScheduleTimeValue(options, selectedValue);
    if (nextLabel) setInputValue(nextLabel);
  }, [isControlled, inputValue, options, selectedValue]);

  const scheduleTimeGroups = useMemo(
    () => groupScheduleTimeOptions(options),
    [options],
  );

  const filteredGroups = useMemo(() => {
    const groups = showAllOptions
      ? scheduleTimeGroups
      : groupScheduleTimeOptions(filterOptionsByQuery(options, inputValue));
    return groupsWithItems(groups);
  }, [inputValue, options, showAllOptions, scheduleTimeGroups]);

  const handleValueChange = (option: ScheduleTimeComboboxOption | null) => {
    const nextValue = option?.value ?? "";
    selectedValueRef.current = option ? option.value : null;
    if (!isControlled) {
      setUncontrolledValue(option ? option.value : null);
    }
    setInputValue(option?.label ?? "");
    onValueChange?.(nextValue);
    setShowAllOptions(false);
  };

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      {label ? (
        <Label
          htmlFor={comboboxInputId}
          variant={labelVariant}
          size={labelSize}
        >
          {label}
        </Label>
      ) : null}

      <Combobox
        autoHighlight={true}
        items={scheduleTimeGroups}
        filteredItems={filteredGroups}
        itemToStringValue={(option) => option.label}
        isItemEqualToValue={(item, selected) => item.value === selected.value}
        value={selectedOption}
        onValueChange={handleValueChange}
        inputValue={inputValue}
        onInputValueChange={(nextValue, eventDetails) => {
          setInputValue(nextValue);
          if (eventDetails.reason === "input-change") {
            setShowAllOptions(false);
          }
          if (nextValue === "" || eventDetails.reason === "clear-press") {
            handleValueChange(null);
          }
        }}
        onOpenChange={(open, eventDetails) => {
          if (open && eventDetails.reason === "trigger-press") {
            setShowAllOptions(true);
          }
          if (!open) {
            setShowAllOptions(false);
            setInputValue(
              labelForScheduleTimeValue(options, selectedValueRef.current),
            );
          }
        }}
        disabled={disabled}
      >
        <ComboboxInput
          id={comboboxInputId}
          className="w-full"
          placeholder={placeholder}
          showClear={clearable}
          showTrigger={true}
          disabled={disabled}
          aria-label={label ? undefined : "Schedule time"}
          aria-invalid={errorMessage ? true : undefined}
          onFocus={() => {
            if (disabled) return;
            setShowAllOptions(true);
          }}
        />
        <ComboboxContent>
          <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
          <ComboboxList>
            {(group, index) => (
              <ComboboxGroup key={group.value} items={group.items}>
                <ComboboxLabel>{group.value}</ComboboxLabel>
                <ComboboxCollection>
                  {(option) => (
                    <ComboboxItem key={option.value} value={option}>
                      {option.label}
                    </ComboboxItem>
                  )}
                </ComboboxCollection>
                {index < filteredGroups.length - 1 ? (
                  <ComboboxSeparator />
                ) : null}
              </ComboboxGroup>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
      {errorMessage ? <FieldErrorText>{errorMessage}</FieldErrorText> : null}
    </div>
  );
};

export default ScheduleTimeCombobox;
